import React from 'react';
import { ClientInvitation } from '../../../../hooks/api/useMyOrganizationClientInvitations';
import { ProfileListItem } from '../../../../primary-record-types/apiTypes/medicalProfileService';
import { AcceptanceArgs, OrganizationInvitationSteps, ProfileResponse } from '../types';
import ConsentStep from './ConsentStep';
import MedicalProfileStep from './MedicalProfileStep';
import PermissionsStep from './PermissionsStep';
import MaxWidthRestrictedContent from '../../../v2BaseComponents/MaxWidthRestrictedContent';
import MainContentContainer from '../../../v2BaseComponents/MainContentContainer';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import { getIntl } from '../../../../utils/intl';
import NonActionableHeader from '../../../v2BaseComponents/Header/NonActionableHeader';
import IntroStep from './IntroStep';
import { AppPermissions } from '../../../../primary-record-types/abac';
import { APITypes } from '../../../../primary-record-types';
import { PRScrollView } from '../../Scroll';

const intl = getIntl('organization-client-invitation', {
    reject: 'Reject Invitation',
    grantAccess: 'Grant Access',
});

export type OrganizationClientInvitationOwnProps = {
    clientInvitation: ClientInvitation;
    profiles: ProfileListItem[];
    onAccept: (args: AcceptanceArgs) => void;
    onReject: () => void;
    onDismiss: () => void;
};

export type OrganizationClientInvitationProps = OrganizationClientInvitationOwnProps;

const OrganizationClientInvitation: React.FC<OrganizationClientInvitationProps> = (props) => {
    const [step, setStep] = React.useState<OrganizationInvitationSteps>('intro');
    const [appPermissions, setAppPermissions] = React.useState<AppPermissions | null>(
        props.clientInvitation.requestedProfilePermissions,
    );
    const [profileResponse, setProfileResponse] = React.useState<ProfileResponse | null>(null);

    const renderStep = () => {
        if (step === 'intro') {
            return (
                <IntroStep
                    clientInvitation={props.clientInvitation}
                    onNext={() =>
                        props.clientInvitation.provisionedMedicalProfileId ? setStep('permissions') : setStep('profile')
                    }
                    onRemindMeLater={props.onDismiss}
                />
            );
        } else if (step === 'profile') {
            return (
                <MedicalProfileStep
                    clientInvitation={props.clientInvitation}
                    profiles={props.profiles}
                    onProfileSubmit={(profileResponse) => {
                        setProfileResponse(profileResponse);
                        setStep('permissions');
                    }}
                />
            );
        } else if (step === 'permissions') {
            return (
                <PermissionsStep
                    defaultPermissions={props.clientInvitation.requestedProfilePermissions || null}
                    onSubmit={(permissions) => {
                        setAppPermissions(permissions);
                        setStep('consent');
                    }}
                />
            );
        } else {
            return (
                <ConsentStep
                    clientInvitation={props.clientInvitation}
                    onConsentComplete={({ fileInputs }) => {
                        props.onAccept({
                            profile: profileResponse!,
                            permissions: appPermissions!,
                            fileInputs: fileInputs
                                .filter((fileInput) => fileInput.tempFileId && fileInput.contentType)
                                .map((_) => _ as APITypes.organizationService.OrganizationClientInvitationFileInput),
                        });
                    }}
                    onReject={props.onReject}
                />
            );
        }
    };

    return (
        <Stack vertical fullWidth fullHeight>
            <NonActionableHeader title={intl('grantAccess')} whiteBackground />
            <PRScrollView style={{ width: '100%', height: '100%' }} contentContainerStyle={{ flexGrow: 1 }}>
                <MaxWidthRestrictedContent variant="sm">
                    <MainContentContainer>
                        <Stack vertical fullWidth space={contentStackSpacing} align="center">
                            {renderStep()}
                        </Stack>
                    </MainContentContainer>
                </MaxWidthRestrictedContent>
            </PRScrollView>
        </Stack>
    );
};

export default React.memo(OrganizationClientInvitation);
